/**
 * Navigation state routers.
 *
 * Ported from @react-navigation/routers 6.1.9 (Base, Stack, Tab and Drawer
 * routers) and trimmed to what MI6 uses. The state shapes and transitions are
 * kept identical because the browser-history integration (./linking.ts) derives
 * push / replace / go(-n) from how these states change, and the E2E journeys
 * record that behaviour exactly.
 */

export type ParamList = Record<string, object | undefined>

export interface Route {
  key: string
  name: string
  params?: any
  path?: string
  state?: NavigationState | PartialState
}

export type HistoryEntry =
  | { type: 'route'; key: string }
  | { type: 'drawer'; status: 'open' | 'closed' }

export interface NavigationState {
  stale: false
  type: string
  key: string
  index: number
  routeNames: string[]
  routes: Route[]
  history?: HistoryEntry[]
  default?: 'open' | 'closed'
}

export interface PartialRoute {
  key?: string
  name: string
  params?: any
  path?: string
  state?: PartialState | NavigationState
}

export interface PartialState {
  stale?: true
  type?: string
  key?: string
  index?: number
  routeNames?: string[]
  routes: PartialRoute[]
  history?: HistoryEntry[]
}

export interface Action {
  type: string
  payload?: any
  source?: string
  target?: string
  [key: string]: any
}

export interface RouterConfigOptions {
  routeNames: string[]
  routeParamList: Record<string, object | undefined>
  routeGetIdList: Record<
    string,
    ((options: { params?: any }) => string | undefined) | undefined
  >
}

export interface Router {
  type: string
  getInitialState(options: RouterConfigOptions): NavigationState
  getRehydratedState(
    partialState: PartialState | NavigationState,
    options: RouterConfigOptions
  ): NavigationState
  getStateForRouteNamesChange(
    state: NavigationState,
    options: RouterConfigOptions & { routeKeyChanges: string[] }
  ): NavigationState
  getStateForRouteFocus(state: NavigationState, key: string): NavigationState
  getStateForAction(
    state: NavigationState,
    action: Action,
    options: RouterConfigOptions
  ): NavigationState | PartialState | null
  shouldActionChangeFocus(action: Action): boolean
  actionCreators: Record<string, (...args: any[]) => Action>
}

export const createKey = () =>
  Math.random().toString(36).slice(2, 10) +
  Math.random().toString(36).slice(2, 10)

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const CommonActions = {
  goBack(): Action {
    return { type: 'GO_BACK' }
  },
  navigate(...args: any[]): Action {
    if (typeof args[0] === 'string') {
      return { type: 'NAVIGATE', payload: { name: args[0], params: args[1] } }
    }
    const payload = args[0] || {}
    if (!payload.hasOwnProperty('key') && !payload.hasOwnProperty('name')) {
      throw new Error(
        'You need to specify name or key when calling navigate with an object as the argument.'
      )
    }
    return { type: 'NAVIGATE', payload }
  },
  reset(state: PartialState | NavigationState | undefined): Action {
    return { type: 'RESET', payload: state }
  },
  setParams(params: object): Action {
    return { type: 'SET_PARAMS', payload: { params } }
  }
}

export const StackActions = {
  replace(name: string, params?: object): Action {
    return { type: 'REPLACE', payload: { name, params } }
  },
  push(name: string, params?: object): Action {
    return { type: 'PUSH', payload: { name, params } }
  },
  pop(count = 1): Action {
    return { type: 'POP', payload: { count } }
  },
  popToTop(): Action {
    return { type: 'POP_TO_TOP' }
  }
}

export const TabActions = {
  jumpTo(name: string, params?: object): Action {
    return { type: 'JUMP_TO', payload: { name, params } }
  }
}

export const DrawerActions = {
  ...TabActions,
  openDrawer(): Action {
    return { type: 'OPEN_DRAWER' }
  },
  closeDrawer(): Action {
    return { type: 'CLOSE_DRAWER' }
  },
  toggleDrawer(): Action {
    return { type: 'TOGGLE_DRAWER' }
  }
}

// ---------------------------------------------------------------------------
// BaseRouter
// ---------------------------------------------------------------------------

const BaseRouter = {
  getStateForAction(
    state: NavigationState,
    action: Action
  ): NavigationState | PartialState | null {
    switch (action.type) {
      case 'SET_PARAMS': {
        const index = action.source
          ? state.routes.findIndex(r => r.key === action.source)
          : state.index

        if (index === -1) {
          return null
        }

        return {
          ...state,
          routes: state.routes.map((r, i) =>
            i === index
              ? { ...r, params: { ...r.params, ...action.payload.params } }
              : r
          )
        }
      }

      case 'RESET': {
        const nextState = action.payload as NavigationState | PartialState

        if (
          nextState.routes.length === 0 ||
          nextState.routes.some(route => !state.routeNames.includes(route.name))
        ) {
          return null
        }

        if (nextState.stale === false) {
          if (
            state.routeNames.length !== nextState.routeNames.length ||
            nextState.routeNames.some(name => !state.routeNames.includes(name))
          ) {
            return null
          }

          return {
            ...nextState,
            routes: nextState.routes.map(route =>
              route.key
                ? route
                : { ...route, key: `${route.name}-${createKey()}` }
            )
          } as NavigationState
        }

        return nextState
      }

      default:
        return null
    }
  },

  shouldActionChangeFocus(action: Action) {
    return action.type === 'NAVIGATE'
  }
}

// ---------------------------------------------------------------------------
// StackRouter
// ---------------------------------------------------------------------------

export function StackRouter(options: { initialRouteName?: string }): Router {
  const router: Router = {
    ...BaseRouter,

    type: 'stack',

    getInitialState({ routeNames, routeParamList }) {
      const initialRouteName =
        options.initialRouteName !== undefined &&
        routeNames.includes(options.initialRouteName)
          ? options.initialRouteName
          : routeNames[0]

      return {
        stale: false,
        type: 'stack',
        key: `stack-${createKey()}`,
        index: 0,
        routeNames,
        routes: [
          {
            key: `${initialRouteName}-${createKey()}`,
            name: initialRouteName,
            params: routeParamList[initialRouteName]
          }
        ]
      }
    },

    getRehydratedState(partialState, { routeNames, routeParamList }) {
      const state = partialState

      if (state.stale === false) {
        return state as NavigationState
      }

      const routes = (state.routes as PartialRoute[])
        .filter(route => routeNames.includes(route.name))
        .map(
          route =>
            ({
              ...route,
              key: route.key || `${route.name}-${createKey()}`,
              params:
                routeParamList[route.name] !== undefined
                  ? { ...routeParamList[route.name], ...route.params }
                  : route.params
            } as Route)
        )

      if (routes.length === 0) {
        const initialRouteName =
          options.initialRouteName !== undefined
            ? options.initialRouteName
            : routeNames[0]

        routes.push({
          key: `${initialRouteName}-${createKey()}`,
          name: initialRouteName,
          params: routeParamList[initialRouteName]
        })
      }

      return {
        stale: false,
        type: 'stack',
        key: `stack-${createKey()}`,
        index: routes.length - 1,
        routeNames,
        routes
      }
    },

    getStateForRouteNamesChange(
      state,
      { routeNames, routeParamList, routeKeyChanges }
    ) {
      const routes = state.routes.filter(
        route =>
          routeNames.includes(route.name) &&
          !routeKeyChanges.includes(route.name)
      )

      if (routes.length === 0) {
        const initialRouteName =
          options.initialRouteName !== undefined &&
          routeNames.includes(options.initialRouteName)
            ? options.initialRouteName
            : routeNames[0]

        routes.push({
          key: `${initialRouteName}-${createKey()}`,
          name: initialRouteName,
          params: routeParamList[initialRouteName]
        })
      }

      return {
        ...state,
        routeNames,
        routes,
        index: Math.min(state.index, routes.length - 1)
      }
    },

    getStateForRouteFocus(state, key) {
      const index = state.routes.findIndex(r => r.key === key)

      if (index === -1 || index === state.index) {
        return state
      }

      return {
        ...state,
        index,
        routes: state.routes.slice(0, index + 1)
      }
    },

    getStateForAction(state, action, options) {
      const { routeParamList } = options

      switch (action.type) {
        case 'REPLACE': {
          const index =
            action.target === state.key && action.source
              ? state.routes.findIndex(r => r.key === action.source)
              : state.index

          if (index === -1) {
            return null
          }

          const { name, key, params } = action.payload

          if (!state.routeNames.includes(name)) {
            return null
          }

          return {
            ...state,
            routes: state.routes.map((route, i) =>
              i === index
                ? {
                    key: key !== undefined ? key : `${name}-${createKey()}`,
                    name,
                    params:
                      routeParamList[name] !== undefined
                        ? { ...routeParamList[name], ...params }
                        : params
                  }
                : route
            )
          }
        }

        case 'PUSH':
          if (state.routeNames.includes(action.payload.name)) {
            const getId = options.routeGetIdList[action.payload.name]
            const id = getId?.({ params: action.payload.params })

            const route = id
              ? state.routes.find(
                  route =>
                    route.name === action.payload.name &&
                    id === getId?.({ params: route.params })
                )
              : undefined

            let routes: Route[]

            if (route) {
              routes = state.routes.filter(r => r.key !== route.key)
              routes.push({
                ...route,
                params:
                  routeParamList[action.payload.name] !== undefined
                    ? {
                        ...routeParamList[action.payload.name],
                        ...action.payload.params
                      }
                    : action.payload.params
              })
            } else {
              routes = [
                ...state.routes,
                {
                  key: `${action.payload.name}-${createKey()}`,
                  name: action.payload.name,
                  params:
                    routeParamList[action.payload.name] !== undefined
                      ? {
                          ...routeParamList[action.payload.name],
                          ...action.payload.params
                        }
                      : action.payload.params
                }
              ]
            }

            return {
              ...state,
              index: routes.length - 1,
              routes
            }
          }

          return null

        case 'POP': {
          const index =
            action.target === state.key && action.source
              ? state.routes.findIndex(r => r.key === action.source)
              : state.index

          if (index > 0) {
            const count = Math.max(index - action.payload.count + 1, 1)
            const routes = state.routes
              .slice(0, count)
              .concat(state.routes.slice(index + 1))

            return {
              ...state,
              index: routes.length - 1,
              routes
            }
          }

          return null
        }

        case 'POP_TO_TOP':
          return router.getStateForAction(
            state,
            { type: 'POP', payload: { count: state.routes.length - 1 } },
            options
          )

        case 'NAVIGATE':
          if (
            action.payload.name !== undefined &&
            !state.routeNames.includes(action.payload.name)
          ) {
            return null
          }

          if (action.payload.key || action.payload.name) {
            let index = -1

            const getId =
              action.payload.key === undefined &&
              action.payload.name !== undefined
                ? options.routeGetIdList[action.payload.name]
                : undefined
            const id = getId?.({ params: action.payload.params })

            if (id) {
              index = state.routes.findIndex(
                route =>
                  route.name === action.payload.name &&
                  id === getId?.({ params: route.params })
              )
            } else if (
              (state.routes[state.index].name === action.payload.name &&
                action.payload.key === undefined) ||
              state.routes[state.index].key === action.payload.key
            ) {
              index = state.index
            } else {
              for (let i = state.routes.length - 1; i >= 0; i--) {
                if (
                  (state.routes[i].name === action.payload.name &&
                    action.payload.key === undefined) ||
                  state.routes[i].key === action.payload.key
                ) {
                  index = i
                  break
                }
              }
            }

            if (
              index === -1 &&
              action.payload.key &&
              action.payload.name === undefined
            ) {
              return null
            }

            if (index === -1 && action.payload.name !== undefined) {
              const routes = [
                ...state.routes,
                {
                  key:
                    action.payload.key ??
                    `${action.payload.name}-${createKey()}`,
                  name: action.payload.name,
                  path: action.payload.path,
                  params:
                    routeParamList[action.payload.name] !== undefined
                      ? {
                          ...routeParamList[action.payload.name],
                          ...action.payload.params
                        }
                      : action.payload.params
                }
              ]

              return {
                ...state,
                routes,
                index: routes.length - 1
              }
            }

            const route = state.routes[index]

            let params

            if (action.payload.merge) {
              params =
                action.payload.params !== undefined ||
                routeParamList[route.name] !== undefined
                  ? {
                      ...routeParamList[route.name],
                      ...route.params,
                      ...action.payload.params
                    }
                  : route.params
            } else {
              params =
                routeParamList[route.name] !== undefined
                  ? { ...routeParamList[route.name], ...action.payload.params }
                  : action.payload.params
            }

            return {
              ...state,
              index,
              routes: [
                ...state.routes.slice(0, index),
                params !== route.params ||
                (action.payload.path && action.payload.path !== route.path)
                  ? {
                      ...route,
                      path: action.payload.path ?? route.path,
                      params
                    }
                  : state.routes[index]
              ]
            }
          }

          return null

        case 'GO_BACK':
          if (state.index > 0) {
            return router.getStateForAction(
              state,
              {
                type: 'POP',
                payload: { count: 1 },
                target: action.target,
                source: action.source
              },
              options
            )
          }

          return null

        default:
          return BaseRouter.getStateForAction(state, action)
      }
    },

    actionCreators: StackActions
  }

  return router
}

// ---------------------------------------------------------------------------
// TabRouter (base of the drawer router)
// ---------------------------------------------------------------------------

type BackBehavior = 'initialRoute' | 'firstRoute' | 'history' | 'order' | 'none'

const TYPE_ROUTE = 'route' as const

const getRouteHistory = (
  routes: Route[],
  index: number,
  backBehavior: BackBehavior,
  initialRouteName: string | undefined
): HistoryEntry[] => {
  const history: HistoryEntry[] = [{ type: TYPE_ROUTE, key: routes[index].key }]
  let initialRouteIndex

  switch (backBehavior) {
    case 'order':
      for (let i = index; i > 0; i--) {
        history.unshift({ type: TYPE_ROUTE, key: routes[i - 1].key })
      }
      break
    case 'firstRoute':
      if (index !== 0) {
        history.unshift({ type: TYPE_ROUTE, key: routes[0].key })
      }
      break
    case 'initialRoute':
      initialRouteIndex = routes.findIndex(
        route => route.name === initialRouteName
      )
      initialRouteIndex = initialRouteIndex === -1 ? 0 : initialRouteIndex

      if (index !== initialRouteIndex) {
        history.unshift({
          type: TYPE_ROUTE,
          key: routes[initialRouteIndex].key
        })
      }
      break
    case 'history':
      // The history will fill up on navigation
      break
  }

  return history
}

const changeIndex = (
  state: NavigationState,
  index: number,
  backBehavior: BackBehavior,
  initialRouteName: string | undefined
): NavigationState => {
  let history

  if (backBehavior === 'history') {
    const currentKey = state.routes[index].key

    history = (state.history || [])
      .filter(it => (it.type === 'route' ? it.key !== currentKey : false))
      .concat({ type: TYPE_ROUTE, key: currentKey })
  } else {
    history = getRouteHistory(
      state.routes,
      index,
      backBehavior,
      initialRouteName
    )
  }

  return {
    ...state,
    index,
    history
  }
}

function TabRouter({
  initialRouteName,
  backBehavior = 'firstRoute'
}: {
  initialRouteName?: string
  backBehavior?: BackBehavior
}): Router {
  const router: Router = {
    ...BaseRouter,

    type: 'tab',

    getInitialState({ routeNames, routeParamList }) {
      const index =
        initialRouteName !== undefined && routeNames.includes(initialRouteName)
          ? routeNames.indexOf(initialRouteName)
          : 0

      const routes = routeNames.map(name => ({
        name,
        key: `${name}-${createKey()}`,
        params: routeParamList[name]
      }))

      const history = getRouteHistory(
        routes,
        index,
        backBehavior,
        initialRouteName
      )

      return {
        stale: false,
        type: 'tab',
        key: `tab-${createKey()}`,
        index,
        routeNames,
        history,
        routes
      }
    },

    getRehydratedState(partialState, { routeNames, routeParamList }) {
      const state = partialState

      if (state.stale === false) {
        return state as NavigationState
      }

      const routes = routeNames.map(name => {
        const route = (state.routes as PartialRoute[]).find(
          r => r.name === name
        )

        return {
          ...route,
          name,
          key:
            route && route.name === name && route.key
              ? route.key
              : `${name}-${createKey()}`,
          params:
            routeParamList[name] !== undefined
              ? {
                  ...routeParamList[name],
                  ...(route ? route.params : undefined)
                }
              : route
              ? route.params
              : undefined
        } as Route
      })

      const index = Math.min(
        Math.max(routeNames.indexOf(state.routes[state?.index ?? 0]?.name), 0),
        routes.length - 1
      )

      const history =
        state.history?.filter(it =>
          routes.find(r => r.key === (it as any).key)
        ) ?? []

      return changeIndex(
        {
          stale: false,
          type: 'tab',
          key: `tab-${createKey()}`,
          index,
          routeNames,
          history,
          routes
        },
        index,
        backBehavior,
        initialRouteName
      )
    },

    getStateForRouteNamesChange(
      state,
      { routeNames, routeParamList, routeKeyChanges }
    ) {
      const routes = routeNames.map(
        name =>
          state.routes.find(
            r => r.name === name && !routeKeyChanges.includes(r.name)
          ) || {
            name,
            key: `${name}-${createKey()}`,
            params: routeParamList[name]
          }
      )

      const index = Math.max(
        0,
        routeNames.indexOf(state.routes[state.index].name)
      )

      let history = (state.history || []).filter(
        it => it.type !== 'route' || routes.find(r => r.key === it.key)
      )

      if (!history.length) {
        history = getRouteHistory(routes, index, backBehavior, initialRouteName)
      }

      return {
        ...state,
        history,
        routeNames,
        routes,
        index
      }
    },

    getStateForRouteFocus(state, key) {
      const index = state.routes.findIndex(r => r.key === key)

      if (index === -1 || index === state.index) {
        return state
      }

      return changeIndex(state, index, backBehavior, initialRouteName)
    },

    getStateForAction(state, action, { routeParamList, routeGetIdList }) {
      switch (action.type) {
        case 'JUMP_TO':
        case 'NAVIGATE': {
          let index = -1

          if (action.type === 'NAVIGATE' && action.payload.key) {
            index = state.routes.findIndex(
              route => route.key === action.payload.key
            )
          } else {
            index = state.routes.findIndex(
              route => route.name === action.payload.name
            )
          }

          if (index === -1) {
            return null
          }

          return changeIndex(
            {
              ...state,
              routes: state.routes.map((route, i) => {
                if (i !== index) {
                  return route
                }

                const getId = routeGetIdList[route.name]

                const currentId = getId?.({ params: route.params })
                const nextId = getId?.({ params: action.payload.params })

                const key =
                  currentId === nextId
                    ? route.key
                    : `${route.name}-${createKey()}`

                let params

                if (
                  action.type === 'NAVIGATE' &&
                  action.payload.merge &&
                  currentId === nextId
                ) {
                  params =
                    action.payload.params !== undefined ||
                    routeParamList[route.name] !== undefined
                      ? {
                          ...routeParamList[route.name],
                          ...route.params,
                          ...action.payload.params
                        }
                      : route.params
                } else {
                  params =
                    routeParamList[route.name] !== undefined
                      ? {
                          ...routeParamList[route.name],
                          ...action.payload.params
                        }
                      : action.payload.params
                }

                const path =
                  action.type === 'NAVIGATE' && action.payload.path != null
                    ? action.payload.path
                    : route.path

                return params !== route.params || path !== route.path
                  ? { ...route, key, path, params }
                  : route
              })
            },
            index,
            backBehavior,
            initialRouteName
          )
        }

        case 'GO_BACK': {
          const history = state.history || []

          if (history.length === 1) {
            return null
          }

          const previousKey = (history[history.length - 2] as any).key
          const index = state.routes.findIndex(
            route => route.key === previousKey
          )

          if (index === -1) {
            return null
          }

          return {
            ...state,
            history: history.slice(0, -1),
            index
          }
        }

        default:
          return BaseRouter.getStateForAction(state, action)
      }
    },

    shouldActionChangeFocus(action) {
      return action.type === 'NAVIGATE'
    },

    actionCreators: TabActions
  }

  return router
}

// ---------------------------------------------------------------------------
// DrawerRouter
// ---------------------------------------------------------------------------

export function DrawerRouter({
  defaultStatus = 'closed',
  ...rest
}: {
  defaultStatus?: 'open' | 'closed'
  initialRouteName?: string
  backBehavior?: BackBehavior
}): Router {
  const router = TabRouter(rest)

  const isDrawerInHistory = (state: NavigationState | PartialState) =>
    Boolean(state.history?.some(it => it.type === 'drawer'))

  const addDrawerToHistory = (state: NavigationState): NavigationState => {
    if (isDrawerInHistory(state)) {
      return state
    }

    return {
      ...state,
      history: [
        ...(state.history || []),
        { type: 'drawer', status: defaultStatus === 'open' ? 'closed' : 'open' }
      ]
    }
  }

  const removeDrawerFromHistory = (state: NavigationState): NavigationState => {
    if (!isDrawerInHistory(state)) {
      return state
    }

    return {
      ...state,
      history: (state.history || []).filter(it => it.type !== 'drawer')
    }
  }

  const openDrawer = (state: NavigationState) => {
    if (defaultStatus === 'open') {
      return removeDrawerFromHistory(state)
    }

    return addDrawerToHistory(state)
  }

  const closeDrawer = (state: NavigationState) => {
    if (defaultStatus === 'open') {
      return addDrawerToHistory(state)
    }

    return removeDrawerFromHistory(state)
  }

  return {
    ...router,

    type: 'drawer',

    getInitialState(options) {
      const state = router.getInitialState(options)

      return {
        ...state,
        default: defaultStatus,
        stale: false,
        type: 'drawer',
        key: `drawer-${createKey()}`
      }
    },

    getRehydratedState(partialState, options) {
      if (partialState.stale === false) {
        return partialState as NavigationState
      }

      let state = router.getRehydratedState(partialState, options)

      if (isDrawerInHistory(partialState)) {
        state = removeDrawerFromHistory(state)
        state = addDrawerToHistory(state)
      }

      return {
        ...state,
        default: defaultStatus,
        type: 'drawer',
        key: `drawer-${createKey()}`
      }
    },

    getStateForRouteFocus(state, key) {
      const result = router.getStateForRouteFocus(state, key)

      return closeDrawer(result)
    },

    getStateForAction(state, action, options) {
      switch (action.type) {
        case 'OPEN_DRAWER':
          return openDrawer(state)

        case 'CLOSE_DRAWER':
          return closeDrawer(state)

        case 'TOGGLE_DRAWER':
          if (isDrawerInHistory(state)) {
            return removeDrawerFromHistory(state)
          }

          return addDrawerToHistory(state)

        case 'JUMP_TO':
        case 'NAVIGATE': {
          const result = router.getStateForAction(state, action, options)

          if (
            result != null &&
            (result as NavigationState).index !== state.index
          ) {
            return closeDrawer(result as NavigationState)
          }

          return result
        }

        case 'GO_BACK':
          if (isDrawerInHistory(state)) {
            return removeDrawerFromHistory(state)
          }

          return router.getStateForAction(state, action, options)

        default:
          return router.getStateForAction(state, action, options)
      }
    },

    actionCreators: DrawerActions
  }
}

export const getDrawerStatusFromState = (
  state: NavigationState
): 'open' | 'closed' => {
  if (state.history == null) {
    throw new Error(
      "Couldn't find the drawer status in the state object. Is it a valid state object of drawer navigator?"
    )
  }

  const entry = state.history.find(it => it.type === 'drawer') as
    | { type: 'drawer'; status: 'open' | 'closed' }
    | undefined

  return entry?.status ?? state.default ?? 'closed'
}
